import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { Redirect, Stack, usePathname } from 'expo-router';
import type { ExtendedStackNavigationOptions } from 'expo-router/build/layouts/StackClient';
import { useEffect } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { Provider as UrqlProvider, useQuery } from 'urql';
import { getUrqlClient, privateExchanges } from '@/client/urqlClient';
import Spinner from '@/components/Custom/Spinner';
import Toast from '@/components/Custom/Toast';
import UpdatesBanner from '@/components/Custom/UpdatesBanner';
import NotifyPopover from '@/components/Notify/NotifyPopover';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import ActionButton from '@/components/Unistyles/ActionButton';
import { graphql } from '@/gql/graphql';
import useAuthStore from '@/stores/useAuthStore';
import useJoinIntentStore from '@/stores/useJoinIntentStore';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';
import modalRoutes from '@/utils/modalRoutes';
import {
  type AccountGateState,
  getPrivateAccountGateRedirect,
  isWelcomeJoinHandoffRoute,
} from '@/utils/privateAccountGate';
import signOut from '@/utils/signOut';

const ACCOUNT_GATE_SPINNER_DELAY_MS = 1500;
const ACCOUNT_GATE_SPINNER_FADE_MS = 250;
const DESKTOP_MODAL_CORNER_RADIUS = 40;

export const unstable_settings = {
  initialRouteName: '(tabs)',
  anchor: '(tabs)',
};

const UnistylesStack = withUnistyles(Stack, (theme) => ({
  screenOptions: {
    contentStyle: {
      backgroundColor: theme.colors.background,
    },
    headerShown: false,
    headerStyle: {
      backgroundColor: theme.colors.background,
    },
  },
}));

const GetPrivateAccountGate = graphql(/* GraphQL */ `
  query GetPrivateAccountGate($id: String!) {
    person_by_pk(id: $id) {
      id
      is_complete
    }
  }
`);

export default function PrivateLayoutRoute() {
  const client = getUrqlClient({
    key: 'private',
    exchanges: privateExchanges,
  });

  return (
    <Box testID="PrivateLayout" flex style={styles.privateLayout}>
      <UpdatesBanner />
      <UrqlProvider value={client}>
        <BottomSheetModalProvider>
          <PrivateAccountGate />
          <Toast />
          <NotifyPopover />
        </BottomSheetModalProvider>
      </UrqlProvider>
    </Box>
  );
}

function PrivateAccountGate() {
  const uid = useAuthStore((state) => state.uid);
  const joinIntent = useJoinIntentStore((state) => state.joinIntent);
  const joinCompletionPending = useJoinIntentStore(
    (state) => state.joinCompletionPending,
  );
  const finishJoinCompletion = useJoinIntentStore(
    (state) => state.finishJoinCompletion,
  );
  const joinResumePending = useJoinIntentStore(
    (state) => state.joinResumePending,
  );
  const pathname = usePathname();
  const [{ data, error, fetching, stale }, refetch] = useQuery({
    query: GetPrivateAccountGate,
    variables: {
      id: uid ?? '',
    },
    pause: !uid,
    requestPolicy: 'network-only',
  });
  const accountId = data?.person_by_pk?.id;
  const isComplete = data?.person_by_pk?.is_complete;
  const didSettleWithoutAccountData =
    Boolean(uid) && !fetching && !stale && !data;
  const isMissingAccount =
    data?.person_by_pk === null ||
    (data?.person_by_pk !== undefined && !accountId) ||
    didSettleWithoutAccountData;
  const accountGateState: AccountGateState =
    isComplete === true
      ? 'complete'
      : isComplete === false
        ? 'incomplete'
        : error || isMissingAccount
          ? 'error'
          : 'loading';
  const showDevConnectionHint = process.env.EXPO_PUBLIC_ENV_NAME === 'dev';
  const isWelcomeHandoff = isWelcomeJoinHandoffRoute(pathname);
  const redirect = getPrivateAccountGateRedirect({
    accountGateState,
    joinCompletionPending,
    joinIntent,
    joinResumePending,
    pathname,
  });

  useEffect(() => {
    if (
      joinCompletionPending &&
      accountGateState === 'complete' &&
      !isWelcomeHandoff
    ) {
      finishJoinCompletion();
    }
  }, [
    accountGateState,
    finishJoinCompletion,
    isWelcomeHandoff,
    joinCompletionPending,
  ]);

  const modalRouteComponents = modalRoutes.map((name) => {
    const modalOptions: ExtendedStackNavigationOptions = {
      contentStyle: styles.modalContent,
      headerStyle: styles.modalHeader,
      presentation: 'modal',
      webModalStyle: {
        minHeight: '80%',
      },
      sheetCornerRadius: isDesktopUserAgent ? DESKTOP_MODAL_CORNER_RADIUS : 0,
    };

    return <Stack.Screen key={name} name={name} options={modalOptions} />;
  });
  const welcomeOptions: ExtendedStackNavigationOptions = {
    contentStyle: styles.welcomeContent,
  };

  if (accountGateState === 'error') {
    return (
      <Box flex center p="xl" space="md">
        <Text center bold size="xl">
          Unable to load your account
        </Text>
        <Text center variant="typographySecondary" selectable>
          Check your connection and try again.
        </Text>
        {showDevConnectionHint ? (
          <Text center variant="typographySecondary" selectable>
            If you are running locally, make sure the backend is reachable.
          </Text>
        ) : null}
        <Box
          direction="horizontal"
          center
          flexWrap="wrap"
          space="sm"
          style={styles.errorActions}
        >
          <ActionButton
            label="Try again"
            icon="refresh-outline"
            variant="secondary"
            onPress={() => refetch({ requestPolicy: 'network-only' })}
          />
          <ActionButton
            label="Sign out"
            icon="log-out-outline"
            variant="secondary"
            onPress={() => {
              void signOut();
            }}
          />
        </Box>
      </Box>
    );
  }

  if (accountGateState === 'loading') {
    return <AccountGateSpinner />;
  }

  if (redirect) {
    return (
      <Redirect href={redirect} withAnchor={accountGateState === 'complete'} />
    );
  }

  return (
    <UnistylesStack>
      <Stack.Protected
        guard={accountGateState === 'incomplete' || joinCompletionPending}
      >
        <Stack.Screen name="welcome" options={welcomeOptions} />
      </Stack.Protected>

      <Stack.Protected
        guard={accountGateState === 'complete' || joinCompletionPending}
      >
        <Stack.Screen name="(tabs)" />
        <Stack.Screen
          name="chat/[chatId]/media"
          options={{ presentation: 'fullScreenModal' }}
        />
      </Stack.Protected>

      <Stack.Protected
        guard={accountGateState === 'complete' && !joinCompletionPending}
      >
        {modalRouteComponents}
      </Stack.Protected>
    </UnistylesStack>
  );
}

function AccountGateSpinner() {
  return (
    <Box flex alignItems="center" justifyContent="center">
      <Animated.View
        entering={FadeIn.delay(ACCOUNT_GATE_SPINNER_DELAY_MS).duration(
          ACCOUNT_GATE_SPINNER_FADE_MS,
        )}
      >
        <Spinner size="lg" />
      </Animated.View>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  modalContent: {
    backgroundColor: theme.colors.surface,
  },
  modalHeader: {
    backgroundColor: theme.colors.surface,
  },
  privateLayout: {
    backgroundColor: theme.colors.background,
  },
  errorActions: {
    maxWidth: '100%',
  },
  welcomeContent: {
    backgroundColor: theme.colors.background,
  },
}));
